/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { Api } from '../../api';
import {
  errorToast,
  successToast
} from '../../utils/helper';
import SearchInput from '../../component/common/Input/SearchInput';
import Table from '../../component/common/Table';
import { Fragment } from 'react';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { PER_PAGE } from '../../utils/constants';
import { Transition, Dialog } from '@headlessui/react';
import Loader from '../../component/common/Loader';
import Pagination from '../../component/common/Pagination/Pagination';
import { StatCard, STAT_ICONS } from '../../component/common/designSystem';

function Partners() {
  const [loader, setLoader] = useState(false);
  const [partnersList, setPartnersList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(-1);
  const [sortBy, setSortBy] = useState('');
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState({});
  const [formData, setFormData] = useState({
    partnerName: '',
    usageLimit: 15000
  });
  const [editFormData, setEditFormData] = useState({
    partnerId: '',
    newLimit: 0
  });

  // Dashboard statistics
  const [totalPartners, setTotalPartners] = useState(0);
  const [activePartners, setActivePartners] = useState(0);
  const [expiredPartners, setExpiredPartners] = useState(0);
  const [totalUsage, setTotalUsage] = useState(0);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  useEffect(() => {
    fetchPartnersData();
  }, [currentPage, selectedPerPage, sortBy, sortOrder]);

  useEffect(() => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const timeout = setTimeout(() => {
      if (searchTerm !== '') {
        setCurrentPage(1);
        fetchPartnersData();
      } else if (searchTerm === '') {
        fetchPartnersData();
      }
    }, 1000);
    setDebounceTimeout(timeout);
  }, [searchTerm]);

  const fetchPartnersData = async () => {
    setLoader(true);
    try {
      const response = await Api.getAPIPartnersList(
        currentPage,
        selectedPerPage.value || selectedPerPage,
        searchTerm,
        '',
        sortBy,
        sortOrder === -1 ? 'desc' : 'asc'
      );
      
      console.log('Full API Response:', response); // Debug log
      console.log('Response Data:', response?.data); // Debug log
      console.log('Response Data Type:', typeof response?.data); // Debug log
      console.log('Response Data Keys:', Object.keys(response?.data || {})); // Debug log
      
      if (response?.data?.meta?.code === 200 || response?.data?.status === 1) {
        // Extract the data based on the actual response structure
        let partnersData = [];
        
        if (response?.data?.data && Array.isArray(response.data.data)) {
          // Standard structure: { data: [...], meta: {...} }
          partnersData = response.data.data;
        } else if (Array.isArray(response?.data)) {
          // Direct array response
          partnersData = response.data;
        } else {
          // Single object response - extract all properties except meta
          const { meta, ...partnerObject } = response.data;
          if (Object.keys(partnerObject).length > 0) {
            partnersData = [partnerObject];
          }
        }
        
        console.log('Final parsed partners data:', partnersData); // Debug log
        console.log('Partners data length:', partnersData.length); // Debug log
        
        setPartnersList(partnersData);
        setTotalCount(response.data.meta?.total || partnersData.length);
        
        // Calculate dashboard stats
        setTotalPartners(partnersData.length);
        setActivePartners(partnersData.filter(p => p.isActive).length);
        setExpiredPartners(partnersData.filter(p => p.isExpired).length);
        setTotalUsage(partnersData.reduce((sum, p) => sum + (p.currentUsage || 0), 0));
        
        console.log('Updated partners list:', partnersData); // Debug log
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      errorToast('Failed to fetch partners data');
    } finally {
      setLoader(false);
    }
  };

  const handleCreatePartner = async () => {
    if (!formData.partnerName.trim()) {
      errorToast('Partner name is required');
      return;
    }

    setLoader(true);
    try {
      const response = await Api.createAPIPartner(formData);
      
      if (response?.data?.meta?.code === 201 || response?.data?.status === 1) {
        successToast('Partner created successfully');
        setShowCreateModal(false);
        setFormData({ partnerName: '', usageLimit: 15000 });
        fetchPartnersData();
      } else {
        errorToast(response?.data?.message || 'Failed to create partner');
      }
    } catch (error) {
      console.error('Error creating partner:', error);
      errorToast('Failed to create partner');
    } finally {
      setLoader(false);
    }
  };

  const handleToggleStatus = async (partnerId, currentStatus) => {
    setLoader(true);
    try {
      const response = await Api.toggleAPIPartnerStatus(partnerId);
      
      if (response?.data?.meta?.code === 200 || response?.data?.status === 1) {
        successToast(`Partner ${currentStatus ? 'deactivated' : 'activated'} successfully`);
        fetchPartnersData();
      } else {
        errorToast(response?.data?.message || 'Failed to update partner status');
      }
    } catch (error) {
      console.error('Error updating partner status:', error);
      errorToast('Failed to update partner status');
    } finally {
      setLoader(false);
    }
  };

  const handleResetUsage = async (partnerId, partnerName) => {
    if (window.confirm(`Reset usage for ${partnerName}?`)) {
      setLoader(true);
      try {
        const response = await Api.resetAPIPartnerUsage({ partnerId });
        
        if (response?.data?.meta?.code === 200 || response?.data?.status === 1) {
          successToast('Partner subscription renewed successfully!');
          fetchPartnersData();
        } else {
          errorToast(response?.data?.message || 'Failed to reset partner usage');
        }
      } catch (error) {
        console.error('Error resetting partner usage:', error);
        errorToast('Failed to reset partner usage');
      } finally {
        setLoader(false);
      }
    }
  };

  const handleEditPartner = (partner) => {
    setSelectedPartner(partner);
    setEditFormData({
      partnerId: partner.partnerId,
      newLimit: partner.usageLimit || 0
    });
    setShowEditModal(true);
  };

  const handleUpdatePartnerLimit = async () => {
    if (!editFormData.newLimit || editFormData.newLimit < 0) {
      errorToast('Please enter a valid usage limit');
      return;
    }

    setLoader(true);
    try {
      const response = await Api.updateAPIPartnerLimit(editFormData);
      
      if (response?.data?.meta?.code === 200 || response?.data?.status === 1) {
        successToast('Partner limit updated successfully');
        setShowEditModal(false);
        setEditFormData({ partnerId: '', newLimit: 0 });
        setSelectedPartner({});
        fetchPartnersData();
      } else {
        errorToast(response?.data?.message || 'Failed to update partner limit');
      }
    } catch (error) {
      console.error('Error updating partner limit:', error);
      errorToast('Failed to update partner limit');
    } finally {
      setLoader(false);
    }
  };

  const handleDeletePartner = async (partnerId, partnerName) => {
    if (window.confirm(`Are you sure you want to delete ${partnerName}?`)) {
      setLoader(true);
      try {
        const response = await Api.deleteAPIPartner(partnerId);
        
        if (response?.data?.meta?.code === 200 || response?.data?.status === 1) {
          successToast('Partner deleted successfully');
          fetchPartnersData();
        } else {
          errorToast(response?.data?.message || 'Failed to delete partner');
        }
      } catch (error) {
        console.error('Error deleting partner:', error);
        errorToast('Failed to delete partner');
      } finally {
        setLoader(false);
      }
    }
  };

  const copyToClipboard = async (partnerKey, partnerName) => {
    try {
      await navigator.clipboard.writeText(partnerKey);
      successToast(`Partner key for ${partnerName} copied to clipboard!`);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      errorToast('Failed to copy partner key to clipboard');
    }
  };

  const dashboardCards = [
    <StatCard
      key="total-partners"
      icon={STAT_ICONS.partner}
      title="Total API Partners"
      value={totalPartners}
    />,
    <StatCard
      key="active-partners"
      icon={STAT_ICONS.partner}
      title="Active API Partners"
      value={activePartners}
    />,
    <StatCard
      key="expired-partners"
      icon={STAT_ICONS.subscription}
      title="Expired Subscriptions"
      value={expiredPartners}
    />,
    <StatCard
      key="total-usage"
      icon={STAT_ICONS.sessions}
      title="Total API Usage"
      value={totalUsage.toLocaleString()}
    />
  ];

  const tableColumns = [
    {
      title: 'Partner Name',
      key: 'partnerName',
      sortKey: 'partnerName',
      sortable: true,
      type: 'text',
      align: 'left',
      width: 250,
      style: {
        opacity: 'opacity-100'
      }
    },
    {
      title: 'Partner Key',
      key: 'partnerKey',
      sortable: false,
      type: 'customTemplate',
      align: 'left',
      customTemplateRender: (rowData) => (
        <button
          onClick={() => copyToClipboard(rowData.fullPartnerKey, rowData.partnerName)}
          className="text-blue-600 hover:text-blue-800 underline cursor-pointer font-mono text-sm"
          title="Click to copy full partner key"
        >
          {rowData.partnerKey}
        </button>
      )
    },
    {
      title: 'Usage Limit',
      key: 'usageLimit',
      sortKey: 'usageLimit',
      sortable: true,
      type: 'number',
      align: 'right'
    },
    {
      title: 'Current Usage',
      key: 'currentUsage',
      sortKey: 'currentUsage',
      sortable: true,
      type: 'number',
      align: 'right'
    },
    {
      title: 'Remaining',
      key: 'remaining',
      sortable: false,
      type: 'number',
      align: 'right'
    },
    {
      title: 'Status',
      key: 'status',
      sortable: false,
      type: 'customTemplate',
      align: 'center',
      customTemplateRender: (rowData) => (
        <div className="flex flex-col space-y-1">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            rowData.isActive 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {rowData.isActive ? 'Active' : 'Inactive'}
          </span>
          {rowData.isExpired && (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
              Expired
            </span>
          )}
        </div>
      )
    },
    {
      title: 'Expires On',
      key: 'expireOn',
      sortKey: 'expireOn',
      sortable: true,
      type: 'customTemplate',
      align: 'left',
      customTemplateRender: (rowData) => (
        <span className={rowData.isExpired ? 'text-red-600 font-semibold' : ''}>
          {rowData.expireOn ? new Date(rowData.expireOn).toLocaleDateString() : 'N/A'}
        </span>
      )
    },
    {
      title: 'Created Date',
      key: 'createdAt',
      sortKey: 'createdAt',
      sortable: true,
      type: 'date',
      align: 'left'
    },
    {
      title: '',
      key: 'action',
      type: 'customTemplate',
      align: 'right',
      customTemplateRender: (rowData) => (
        <div className="flex items-center justify-end">
          <button
            onClick={() => handleEditPartner(rowData)}
            className="w-[20px] ml-2 text-shoorah-secondary dark:text-white cursor-pointer hover:text-blue-700"
            title="Edit Partner Limit"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          </button>
          
          <button
            onClick={() => handleToggleStatus(rowData.partnerId, rowData.isActive)}
            className="w-[20px] ml-2 cursor-pointer hover:text-blue-700"
            title={rowData.isActive ? 'Deactivate Partner' : 'Activate Partner'}
          >
            {rowData.isActive ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-red-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-green-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
            )}
          </button>
          
          <button
            onClick={() => handleResetUsage(rowData.partnerId, rowData.partnerName)}
            className="w-[20px] ml-2 text-green-600 cursor-pointer hover:text-green-700"
            title="Reset Usage & Extend Subscription by 1 Year (carries forward unused APIs)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
          </button>
          
          <button
            onClick={() => handleDeletePartner(rowData.partnerId, rowData.partnerName)}
            className="w-[20px] ml-2 text-red-500 cursor-pointer hover:text-red-700"
            title="Delete Partner"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </div>
      )
    }
  ];

  console.log('Partners list for table:', partnersList); // Debug log
  console.log('Partners list length:', partnersList.length); // Debug log
  
  // Transform data to include computed fields and proper formatting
  const tableData = partnersList.map((partner) => {
    console.log('Mapping partner:', partner); // Debug log
    return {
      ...partner,
      fullPartnerKey: partner.partnerKey || 'N/A', // Store full key for copying
      partnerKey: partner.partnerKey ? `pk_...${partner.partnerKey.slice(-8)}` : 'N/A', // Display truncated version
      remaining: (partner.usageLimit || 0) - (partner.currentUsage || 0)
    };
  });

  return (
    <Fragment>
      <Helmet>
        <title>API Partners Management | Shoorah Admin</title>
      </Helmet>

      {/* Create Partner Modal */}
      <Transition appear show={showCreateModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setShowCreateModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Create New API Partner
                  </Dialog.Title>
                  <div className="mt-4">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Partner Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.partnerName}
                        onChange={(e) =>
                          setFormData({ ...formData, partnerName: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter API partner name (e.g., 'Insurance Co', 'Healthcare Partner')"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Usage Limit
                      </label>
                      <input
                        type="number"
                        value={formData.usageLimit}
                        onChange={(e) =>
                          setFormData({ ...formData, usageLimit: parseInt(e.target.value) || 0 })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="15000"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none"
                      onClick={handleCreatePartner}
                    >
                      Create Partner
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Partner Modal */}
      <Transition appear show={showEditModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setShowEditModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Edit Partner Limit
                  </Dialog.Title>
                  <div className="mt-4">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Partner Name
                      </label>
                      <input
                        type="text"
                        value={selectedPartner.partnerName || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Usage: {selectedPartner.currentUsage?.toLocaleString() || 0}
                      </label>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expires On: {selectedPartner.expireOn ? new Date(selectedPartner.expireOn).toLocaleDateString() : 'N/A'}
                        {selectedPartner.isExpired && <span className="text-red-500 ml-2">(Expired)</span>}
                      </label>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Usage Limit <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={editFormData.newLimit}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, newLimit: parseInt(e.target.value) || 0 })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="15000"
                        min="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Note: Updating usage limit does not change the expiry date. Use "Reset Usage" to extend subscription.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none"
                      onClick={() => setShowEditModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none"
                      onClick={handleUpdatePartnerLimit}
                    >
                      Update Limit
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <div className="relative lg:ps-5 lg:mb-[45px]">
        <Helmet>
          <meta charSet="utf-8" />
          <title>API Partners Management | Shoorah Admin</title>
        </Helmet>
        {loader ? <Loader /> : null}
      </div>
      <div className="relative lg:mb-[45px]">
          {/* Dashboard Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {dashboardCards}
          </div>

          {/* Controls */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <SearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search API partners..."
              />
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add New API Partner
            </button>
          </div>

          {/* Partners Table */}
          <Table
            columns={tableColumns}
            data={tableData}
            name={'api_partners_table'}
            bottomBorder={totalCount > (selectedPerPage?.value || selectedPerPage)}
            setSortBy={(sort) => setSortBy(sort)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            loader={loader}
          />

          {/* Pagination */}
          {totalCount > (selectedPerPage.value || selectedPerPage) && (
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              selectedPerPage={selectedPerPage}
              totalCount={totalCount}
            />
          )}
        </div>
    </Fragment>
  );
}

export default Partners;